"use client";

import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { useCreateSupplier, useSuppliers } from "@/hooks/api/useSuppliers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SuppliersPage() {
  const [q, setQ] = useState("");
  const { data: suppliers = [], isLoading, isError, error } = useSuppliers(q || undefined);
  const create = useCreateSupplier();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gstin, setGstin] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  async function onCreate() {
    setSaveError(null);
    if (!name.trim()) {
      setSaveError("Name required.");
      return;
    }
    try {
      await create.mutateAsync({
        name,
        phone,
        gstin,
        email,
        address,
        is_active: 1,
      });
      setOpen(false);
      setName("");
      setPhone("");
      setGstin("");
      setEmail("");
      setAddress("");
    } catch (e: unknown) {
      let msg = "Could not create supplier.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string; message?: string } | undefined;
        msg = data?.error ?? data?.message ?? msg;
      }
      setSaveError(msg);
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Suppliers</h1>
        <Button type="button" onClick={() => setOpen(true)}>
          Add supplier
        </Button>
      </div>
      <Input className="max-w-sm" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive" role="alert">
          {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to load suppliers."}
        </p>
      )}
      <div className="grid gap-2 md:grid-cols-2">
        {suppliers.map((s) => (
          <Card key={s.id} className="p-4">
            <Link to={`/suppliers/${s.id}`} className="font-medium hover:underline">
              {s.name}
            </Link>
            <p className="text-xs text-muted-foreground">{s.phone ?? "—"} · {s.gstin ?? "—"}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">{s.type ?? "distributor"}</Badge>
              {s.is_active === 0 && <Badge variant="destructive">inactive</Badge>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add supplier</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>GSTIN</Label>
              <Input value={gstin} onChange={(e) => setGstin(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>

            {saveError ? (
              <p className="text-sm text-destructive" role="alert">
                {saveError}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={create.isPending} onClick={() => void onCreate()}>
                {create.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
